import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Minus, Plus, ChevronLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

// Detect if we're on Emergent preview (use backend) or elsewhere like Netlify (use local /api)
const isEmergentPreview = typeof window !== 'undefined' && window.location.hostname.includes('emergentagent.com');
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Only use backend URL on Emergent preview, otherwise use relative /api path for Netlify Functions
const API = (isEmergentPreview && BACKEND_URL) ? `${BACKEND_URL}/api` : '/api';

export const ProductDetail = () => {
  const { handle } = useParams();
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isAdding, setIsAdding] = useState(false);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API}/products/${handle}`);
        setProduct(response.data);
        // Set default variant
        if (response.data.variants?.length > 0) {
          const firstAvailable = response.data.variants.find(v => v.available) || response.data.variants[0];
          setSelectedVariant(firstAvailable);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [handle]);

  const handleAddToCart = async () => {
    if (!selectedVariant) {
      toast.error('Please select a size');
      return;
    }

    setIsAdding(true);
    try {
      await addToCart(selectedVariant.id, quantity);
      toast.success(`${product.title} added to cart!`);
    } catch (err) {
      toast.error('Failed to add to cart');
    } finally {
      setIsAdding(false);
    }
  };

  const handleBuyNow = async () => {
    if (!selectedVariant) {
      toast.error('Please select a size');
      return;
    }

    setIsAdding(true);
    try {
      const cart = await addToCart(selectedVariant.id, quantity);
      if (cart?.checkoutUrl) {
        window.location.href = cart.checkoutUrl;
      }
    } catch (err) {
      toast.error('Failed to proceed to checkout');
      setIsAdding(false);
    }
  };

  // Extract size options
  const getSizeOptions = () => {
    if (!product?.variants) return [];
    const sizes = new Set();
    product.variants.forEach(v => {
      const sizeOption = v.options?.find(o => o.name.toLowerCase() === 'size');
      if (sizeOption) {
        sizes.add(sizeOption.value);
      }
    });
    return Array.from(sizes);
  };

  const sizeOptions = getSizeOptions();

  const getVariantBySize = (size) => {
    return product?.variants?.find(v => {
      const sizeOption = v.options?.find(o => o.name.toLowerCase() === 'size');
      return sizeOption?.value === size;
    });
  };

  if (isLoading) {
    return (
      <main className="pt-20" data-testid="product-loading">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Skeleton className="aspect-square w-full" />
            <div className="space-y-6">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-8 w-1/4" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="pt-20" data-testid="product-error">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 text-center">
          <h1 className="font-headings text-3xl text-wild-dark mb-4">
            PRODUCT NOT FOUND
          </h1>
          <p className="font-body text-wild-stone mb-8">
            {error || 'The product you are looking for does not exist.'}
          </p>
          <Link to="/shop">
            <Button className="bg-wild-leather text-white hover:bg-wild-leather/90">
              BACK TO SHOP
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  const images = product.images?.length > 0 ? product.images : [product.image];
  const price = parseFloat(product.price || 34.99).toFixed(2);

  return (
    <>
      <Helmet>
        <title>{product.title} | Wild Ones LLC</title>
        <meta
          name="description"
          content={product.description || `Shop ${product.title} - Premium southern-style apparel from Wild Ones LLC.`}
        />
        <link rel="canonical" href={`https://wildones.com/product/${handle}`} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.title} | Wild Ones LLC`} />
        <meta property="og:image" content={product.image} />
        <meta property="product:price:amount" content={price} />
        <meta property="product:price:currency" content="USD" />
      </Helmet>

      <main className="pt-[116px]" data-testid="product-detail-page">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
          {/* Breadcrumb */}
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 font-body text-wild-stone hover:text-wild-leather transition-colors mb-8"
            data-testid="back-to-shop"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Shop
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Product Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square bg-wild-khaki/20 overflow-hidden">
                <img
                  src={images[selectedImage]}
                  alt={product.title}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              </div>

              {/* Thumbnail Gallery */}
              {images.length > 1 && (
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 flex-shrink-0 border-2 transition-colors ${
                        selectedImage === index
                          ? 'border-wild-leather'
                          : 'border-transparent hover:border-wild-khaki'
                      }`}
                      data-testid={`product-thumbnail-${index}`}
                    >
                      <img
                        src={img}
                        alt={`${product.title} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="lg:py-4">
              {/* Title */}
              <h1
                className="font-headings text-3xl md:text-4xl tracking-wider text-wild-dark mb-4"
                data-testid="product-title"
              >
                {product.title}
              </h1>

              {/* Price */}
              <p
                className="font-body text-2xl font-semibold text-wild-dark mb-6"
                data-testid="product-price"
              >
                ${price}
              </p>

              {/* Description */}
              {product.description && (
                <p className="font-body text-wild-stone mb-8 leading-relaxed">
                  {product.description}
                </p>
              )}

              {/* Size Selector */}
              {sizeOptions.length > 0 && (
                <div className="mb-6">
                  <label className="block font-headings text-sm tracking-wider text-wild-dark mb-3">
                    SIZE
                  </label>
                  <div className="flex flex-wrap gap-3" data-testid="size-selector">
                    {sizeOptions.map((size) => {
                      const variant = getVariantBySize(size);
                      const isSelected = selectedVariant?.options?.some(
                        o => o.name.toLowerCase() === 'size' && o.value === size
                      );
                      const isAvailable = variant?.available;

                      return (
                        <button
                          key={size}
                          onClick={() => variant && setSelectedVariant(variant)}
                          disabled={!isAvailable}
                          className={`px-6 py-3 font-body text-sm border transition-all ${
                            isSelected
                              ? 'border-wild-leather bg-wild-leather text-white'
                              : isAvailable
                              ? 'border-wild-khaki text-wild-dark hover:border-wild-leather'
                              : 'border-wild-khaki/50 text-wild-stone/50 cursor-not-allowed line-through'
                          }`}
                          data-testid={`size-${size}`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quantity Selector */}
              <div className="mb-8">
                <label className="block font-headings text-sm tracking-wider text-wild-dark mb-3">
                  QUANTITY
                </label>
                <div className="flex items-center gap-4" data-testid="quantity-selector">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 flex items-center justify-center border border-wild-khaki hover:border-wild-leather transition-colors"
                    data-testid="decrease-quantity"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="font-body text-lg w-12 text-center">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 flex items-center justify-center border border-wild-khaki hover:border-wild-leather transition-colors"
                    data-testid="increase-quantity"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <Button
                  onClick={handleAddToCart}
                  disabled={isAdding || !selectedVariant}
                  className="w-full bg-wild-leather text-white hover:bg-wild-leather/90 py-6 text-lg font-headings tracking-widest"
                  data-testid="add-to-cart-btn"
                >
                  {isAdding ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : null}
                  ADD TO CART
                </Button>

                <Button
                  onClick={handleBuyNow}
                  disabled={isAdding || !selectedVariant}
                  variant="outline"
                  className="w-full border-2 border-wild-dark text-wild-dark hover:bg-wild-dark hover:text-white py-6 text-lg font-headings tracking-widest"
                  data-testid="buy-now-btn"
                >
                  BUY NOW
                </Button>
              </div>

              {/* Product Details */}
              <div className="mt-10 pt-8 border-t border-wild-khaki">
                <h3 className="font-headings text-lg tracking-wider mb-4">
                  PRODUCT DETAILS
                </h3>
                <ul className="font-body text-wild-stone space-y-2">
                  <li>• Premium quality cotton blend</li>
                  <li>• Comfortable fit for all-day wear</li>
                  <li>• Machine washable</li>
                  <li>• Designed & printed in the USA</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};
